import { useEffect, useMemo, useState } from "react";
import { Field, SelectField, Stack } from "@leylion/design-system";
import { searchCashierCustomers } from "../api/client";
import type { Customer } from "../api/cashier-types";

type Language = "ar" | "en";

type Props = {
  language: Language;
  label: string;
  value: string;
  initialCustomers: Customer[];
  selectedCustomer?: Customer | null;
  onSelect: (customer: Customer | null) => void;
  emptyLabel: string;
  searchTestId: string;
  selectTestId: string;
  enabled?: boolean;
};

const SEARCH_DELAY_MS = 250;

function customerLabel(customer: Customer): string {
  const company = customer.companyName && customer.companyName !== customer.name
    ? customer.companyName
    : null;
  return [
    customer.name,
    company,
    customer.phone ?? customer.customerNumber,
    customer.taxRegistrationNumber ? `TRN ${customer.taxRegistrationNumber}` : null
  ]
    .filter(Boolean)
    .join(" · ");
}

export function CashierCustomerSearchPicker({
  language,
  label,
  value,
  initialCustomers,
  selectedCustomer,
  onSelect,
  emptyLabel,
  searchTestId,
  selectTestId,
  enabled = true
}: Props) {
  const ar = language === "ar";
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Customer[]>(initialCustomers);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!query.trim()) setResults(initialCustomers);
  }, [initialCustomers, query]);

  useEffect(() => {
    const term = query.trim();
    if (!enabled || !term) {
      setLoading(false);
      setError("");
      if (!term) setResults(initialCustomers);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError("");
      searchCashierCustomers(term)
        .then((items) => {
          if (!cancelled) setResults(items);
        })
        .catch((caught) => {
          if (cancelled) return;
          setError(caught instanceof Error ? caught.message : "Customer search failed.");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, SEARCH_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [enabled, initialCustomers, query]);

  const options = useMemo(() => {
    if (!selectedCustomer || results.some((item) => item.id === selectedCustomer.id)) {
      return results;
    }
    return [selectedCustomer, ...results];
  }, [results, selectedCustomer]);

  return (
    <Stack>
      <Field
        data-testid={searchTestId}
        label={ar ? "بحث العميل" : "Customer search"}
        placeholder={
          ar
            ? "الاسم، الهاتف، الشركة أو الرقم الضريبي"
            : "Name, phone, company or TRN"
        }
        value={query}
        autoComplete="off"
        disabled={!enabled}
        onChange={(event) => setQuery(event.target.value)}
      />
      <SelectField
        data-testid={selectTestId}
        label={label}
        value={value}
        disabled={!enabled}
        onChange={(event) => {
          const customerId = event.target.value;
          const customer = options.find((item) => item.id === customerId);
          if (customerId && customerId === value && !customer) return;
          onSelect(customer ?? null);
        }}
      >
        <option value="">{emptyLabel}</option>
        {value && !options.some((customer) => customer.id === value) ? (
          <option value={value}>{ar ? "العميل المرتبط بالطلب" : "Customer attached to order"}</option>
        ) : null}
        {options.map((customer) => (
          <option key={customer.id} value={customer.id}>
            {customerLabel(customer)}
          </option>
        ))}
      </SelectField>
      {loading ? (
        <small data-testid={`${searchTestId}-status`}>
          {ar ? "جارٍ البحث…" : "Searching…"}
        </small>
      ) : error ? (
        <small data-testid={`${searchTestId}-error`}>{error}</small>
      ) : query.trim() && results.length === 0 ? (
        <small data-testid={`${searchTestId}-empty`}>
          {ar ? "لا توجد نتائج مطابقة." : "No matching customers."}
        </small>
      ) : null}
    </Stack>
  );
}
